/**
 * hq-bundle-pricing
 *
 * The bundle set arithmetic the product page shows, kept free of the DOM so it
 * can be tested in Node. The checkout discount comes from the app's Discount
 * Function (hairqueenla-bundle-pricing/extensions/bundle-tier-discount), which
 * implements the same rules in src/bundle_pricing.js; a shared table of cases
 * runs through both (theme-tests/parity.test.js) so "Your price" is what
 * checkout charges, to the cent.
 *
 * Tiers come from the product metafield custom.bundle_tier_discounts, e.g.
 * {"2":5,"3":10,"4":12,"5":15}. A missing or malformed value means no tiers —
 * the same as the Function, which then gives no discount.
 *
 * All money is integer cents.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HqBundlePricing = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /** Metafield JSON -> sorted [{ qty, percent }]; anything invalid is dropped. */
  function normalizeTiers(raw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return [];
    var tiers = [];
    Object.keys(raw).forEach(function (key) {
      if (!/^\d+$/.test(key)) return;
      var qty = parseInt(key, 10);
      var value = raw[key];
      var percent = typeof value === 'number' ? value : NaN;
      if (qty < 1 || !isFinite(percent) || percent <= 0 || percent > 100) return;
      tiers.push({ qty: qty, percent: Math.round(percent * 100) / 100 });
    });
    return tiers.sort(function (a, b) { return a.qty - b.qty; });
  }

  /** Highest tier at or below the size (so sizes above the top tier are capped). */
  function tierPercent(tiers, size) {
    var percent = 0;
    for (var i = 0; i < tiers.length; i += 1) {
      if (tiers[i].qty <= size) percent = tiers[i].percent;
    }
    return percent;
  }

  /* ISO 4217 currencies with no minor unit: prices still arrive in
     hundredths, but a discount must be a whole unit (the Function agrees). */
  var ZERO_DECIMAL = ['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];

  /** Smallest chargeable step in hundredths: 1 (a cent) or 100 (a whole unit). */
  function currencyStep(currencyCode) {
    return ZERO_DECIMAL.indexOf(String(currencyCode || '').toUpperCase()) > -1 ? 100 : 1;
  }

  /** Discount on a subtotal, rounded half up to the cent (or whole unit). */
  function setDiscountCents(subtotalCents, percent, step) {
    var s = step || 1;
    if (!(subtotalCents > 0) || !(percent > 0)) return 0;
    var hundredths = Math.round(percent * 100);
    var units = Math.floor((subtotalCents * hundredths + s * 5000) / (s * 10000));
    return Math.min(units * s, subtotalCents);
  }

  /** Largest-remainder split of total across weights; ties to the earlier one. */
  function allocateCents(totalCents, weights) {
    if (weights.length === 1) return [weights[0] > 0 && totalCents > 0 ? totalCents : 0];
    var sum = weights.reduce(function (a, b) { return a + b; }, 0);
    if (!(totalCents > 0) || !(sum > 0)) return weights.map(function () { return 0; });
    var shares = weights.map(function (w, i) {
      var exact = totalCents * w;
      return { i: i, base: Math.floor(exact / sum), rem: exact % sum };
    });
    var left = totalCents - shares.reduce(function (a, s) { return a + s.base; }, 0);
    shares.slice().sort(function (a, b) { return (b.rem - a.rem) || (a.i - b.i); }).forEach(function (s) {
      if (left <= 0) return;
      s.base += 1;
      left -= 1;
    });
    return shares.map(function (s) { return s.base; });
  }

  /**
   * Price a set given one entry per bundle or per cart line:
   * [{ subtotalCents, quantity }]. Same result either way.
   */
  function priceSet(lines, tiers, step) {
    var s = step || 1;
    var size = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
    var subtotalCents = lines.reduce(function (a, l) { return a + l.subtotalCents; }, 0);
    var percent = tierPercent(tiers, size);
    var discountCents = setDiscountCents(subtotalCents, percent, s);
    return {
      size: size,
      percent: percent,
      subtotalCents: subtotalCents,
      discountCents: discountCents,
      totalCents: subtotalCents - discountCents,
      allocations: allocateCents(discountCents / s, lines.map(function (l) { return l.subtotalCents; }))
        .map(function (u) { return u * s; }),
    };
  }

  /** One bundle at a tier, for the per-bundle figures on the tier rows. */
  function unitNet(unitCents, percent, step) {
    return unitCents - setDiscountCents(unitCents, percent, step);
  }

  /**
   * Read the store's money format from a sample Liquid printed for 1234567
   * cents ("$12,345.67", "₴12 345,67", "$12,346" ...).
   */
  function parseMoneySample(sample) {
    var text = String(sample || '');
    var run = text.match(/\d[\d.,'’\s  ]*\d|\d/);
    if (!run) return null;
    var num = run[0];
    var dec = num.match(/[.,](\d{2})$/);
    var intPart = dec ? num.slice(0, -3) : num;
    var group = intPart.match(/[^\d]/);
    return {
      prefix: text.slice(0, run.index),
      suffix: text.slice(run.index + num.length),
      decSep: dec ? num.charAt(num.length - 3) : '',
      groupSep: group ? group[0] : '',
      decimals: dec ? 2 : 0,
    };
  }

  /**
   * Format cents in the store's style. A format without decimals still shows
   * cents when there are any: checkout always charges to the cent, and a
   * page that rounds $202.50 to $203 quotes a price nobody pays.
   */
  function formatMoney(cents, spec) {
    var n = Math.round(cents || 0);
    var neg = n < 0;
    n = Math.abs(n);
    var sign = neg ? '-' : '';
    if (!spec) return sign + '$' + (n / 100).toFixed(2);
    var showCents = spec.decimals > 0 || n % 100 !== 0;
    var decSep = spec.decSep || (spec.groupSep === '.' ? ',' : '.');
    var parts = (showCents ? (n / 100).toFixed(2) : String(n / 100)).split('.');
    var whole = spec.groupSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, spec.groupSep) : parts[0];
    return sign + spec.prefix + whole + (showCents ? decSep + parts[1] : '') + spec.suffix;
  }

  /** Unique per add. Only a grouping key: the Function trusts nothing else in it. */
  function makeBundleId(now, random) {
    var t = (now == null ? Date.now() : now).toString(36);
    var r = (random == null ? Math.random() : random).toString(36).slice(2, 8);
    return 'HQB-' + t + '-' + r;
  }

  /**
   * The /cart/add.js items for one set.
   *
   * Identical picks collapse into one line with a quantity (Shopify would merge
   * same-variant same-property lines anyway). Every set line carries the same
   * _bundle_id and _bundle_size; add-ons are linked with _bundle_addon_for and
   * never carry _bundle_id, so they are never counted as part of the set.
   *
   * @param {{variantIds: number[], bundleId: string, label?: string, addonIds?: number[]}} opts
   */
  function buildCartItems(opts) {
    var size = String(opts.variantIds.length);
    var grouped = [];
    var byId = {};
    opts.variantIds.forEach(function (id) {
      if (byId[id]) {
        byId[id].quantity += 1;
        return;
      }
      var properties = { _bundle_id: opts.bundleId, _bundle_size: size };
      if (opts.label) properties.Set = opts.label;
      byId[id] = { id: id, quantity: 1, properties: properties };
      grouped.push(byId[id]);
    });
    (opts.addonIds || []).forEach(function (id) {
      if (id) grouped.push({ id: id, quantity: 1, properties: { _bundle_addon_for: opts.bundleId } });
    });
    return grouped;
  }

  return {
    currencyStep: currencyStep,
    normalizeTiers: normalizeTiers,
    tierPercent: tierPercent,
    setDiscountCents: setDiscountCents,
    allocateCents: allocateCents,
    priceSet: priceSet,
    unitNet: unitNet,
    parseMoneySample: parseMoneySample,
    formatMoney: formatMoney,
    makeBundleId: makeBundleId,
    buildCartItems: buildCartItems,
  };
}));
